import { ThumbnailImageMetadata } from './types';

export function generateFallbackThumbnailDisplayInfo(durationMs = 30000): ThumbnailImageMetadata {
  const height = 144;
  const width = 256;
  const numSecondsInSegment = Math.floor(durationMs / 1000);

  return {
    interval: 1,
    rows: 1,
    columns: numSecondsInSegment,
    height,
    width: width * numSecondsInSegment,
  };
}

/**
 * Calculate the display ratio for scaling thumbnails to a target height.
 * Uses per-frame (row) height so multi-row sprite sheets match px-mode filmstrip sizing.
 */
export function calculateThumbnailDisplayRatio(
  metadata: ThumbnailImageMetadata,
  targetHeight: number,
): number {
  const frameNaturalHeight = metadata.height / Math.max(1, metadata.rows);
  return targetHeight / frameNaturalHeight;
}

/**
 * Calculate the width of a single frame in a thumbnail sprite
 */
export function calculateThumbnailFrameWidth(
  metadata: ThumbnailImageMetadata,
  displayRatio: number,
): number {
  return (metadata.width / Math.max(1, metadata.columns)) * displayRatio;
}

/**
 * Calculate the total width of the scaled sprite image
 */
export function calculateThumbnailSpriteWidth(
  metadata: ThumbnailImageMetadata,
  displayRatio: number,
): number {
  return metadata.width * displayRatio;
}

/**
 * Calculate the total height of the scaled sprite image
 */
export function calculateThumbnailSpriteHeight(
  metadata: ThumbnailImageMetadata,
  displayRatio: number,
): number {
  return metadata.height * displayRatio;
}

/**
 * Calculate all thumbnail dimensions at once for convenience
 */
export function calculateThumbnailDimensions(
  metadata: ThumbnailImageMetadata,
  targetHeight: number,
) {
  const displayRatio = calculateThumbnailDisplayRatio(metadata, targetHeight);
  const frameWidth = calculateThumbnailFrameWidth(metadata, displayRatio);
  const spriteWidth = calculateThumbnailSpriteWidth(metadata, displayRatio);
  const spriteHeight = calculateThumbnailSpriteHeight(metadata, displayRatio);

  return {
    displayRatio,
    frameWidth,
    spriteWidth,
    spriteHeight,
  };
}

/**
 * Total number of frames in a sprite sheet.
 */
export function getThumbnailFrameCount(metadata: ThumbnailImageMetadata): number {
  return Math.max(1, metadata.columns * metadata.rows);
}

/**
 * Maps a segment-relative offset in seconds to a clamped sprite frame index.
 * Honors metadata.interval (seconds between frames) and multi-row frame counts.
 */
export function getThumbnailFrameIndexFromOffsetSeconds(
  offsetSeconds: number,
  metadata: ThumbnailImageMetadata,
): number {
  const interval = Math.max(1, metadata.interval || 1);
  const frameCount = getThumbnailFrameCount(metadata);
  const rawIndex = Math.floor(offsetSeconds / interval);
  return Math.min(frameCount - 1, Math.max(0, rawIndex));
}

/**
 * Picks the first defined thumbnail metadata, then falls back to a synthetic strip.
 * Optional `fallbackDurationMs` sizes the synthetic strip when no candidate is present.
 */
export function resolveThumbnailMetadata(
  candidates: Array<ThumbnailImageMetadata | undefined | null>,
  fallbackDurationMs?: number,
): ThumbnailImageMetadata {
  for (const candidate of candidates) {
    if (candidate) {
      return candidate;
    }
  }
  return generateFallbackThumbnailDisplayInfo(fallbackDurationMs);
}

export type ThumbnailSpriteMode = 'px' | 'fill';

export type ThumbnailFrameBackgroundStyles = {
  backgroundSize: string;
  backgroundPosition: string;
  backgroundRepeat: 'no-repeat';
  width?: string;
  height?: string;
};

/**
 * Returns CSS background styles for a single sprite frame.
 * - `fill`: percentages so one frame covers the parent box (card hover).
 * - `px`: fixed filmstrip cell sized from `targetHeight` (logger thumbs).
 */
export function getThumbnailFrameBackground(
  metadata: ThumbnailImageMetadata,
  frameIndex: number,
  mode: ThumbnailSpriteMode,
  targetHeight?: number,
): ThumbnailFrameBackgroundStyles {
  const frameCount = getThumbnailFrameCount(metadata);
  const safeIndex = ((frameIndex % frameCount) + frameCount) % frameCount;
  const col = safeIndex % metadata.columns;
  const row = Math.floor(safeIndex / metadata.columns);

  if (mode === 'fill') {
    const xPercent = metadata.columns <= 1 ? 0 : (col / (metadata.columns - 1)) * 100;
    const yPercent = metadata.rows <= 1 ? 0 : (row / (metadata.rows - 1)) * 100;

    return {
      backgroundSize: `${metadata.columns * 100}% ${metadata.rows * 100}%`,
      backgroundPosition: `${xPercent}% ${yPercent}%`,
      backgroundRepeat: 'no-repeat',
      width: '100%',
      height: '100%',
    };
  }

  // px mode: scale so one frame row matches targetHeight (1-row matches legacy filmstrip math).
  const frameNaturalHeight = metadata.height / Math.max(1, metadata.rows);
  const height = targetHeight ?? frameNaturalHeight;
  const displayRatio = height / frameNaturalHeight;
  const frameWidth = (metadata.width / Math.max(1, metadata.columns)) * displayRatio;
  const frameHeight = height;
  const spriteWidth = metadata.width * displayRatio;
  const spriteHeight = metadata.height * displayRatio;

  return {
    backgroundSize: `${spriteWidth}px ${spriteHeight}px`,
    backgroundPosition: `-${col * frameWidth}px -${row * frameHeight}px`,
    backgroundRepeat: 'no-repeat',
    width: `${frameWidth}px`,
    height: `${frameHeight}px`,
  };
}
